import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  formatDate,
  getISOWeek,
  getISOWeekYear,
  parseISO,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';

import { DateFormat, Resolution } from '../enums/resolution.enum';

export const dateWithoutTimezone = (date: Date) => {
  const tzoffset = date.getTimezoneOffset() * 60000;
  const withoutTimezone = new Date(date.valueOf() - tzoffset);
  return withoutTimezone;
};

export const unixTimeToDesiredResolutionUnixTime = (
  unixTime: number,
  desiredResolution: Resolution,
) => {
  const date = new Date(unixTime);
  switch (desiredResolution) {
    case Resolution.Daily:
      return date.getTime();
    case Resolution.Weekly:
      return dateWithoutTimezone(
        startOfWeek(date, { weekStartsOn: 1 }),
      ).getTime();
    case Resolution.Monthly:
      return dateWithoutTimezone(startOfMonth(date)).getTime();
    case Resolution.Yearly:
      return dateWithoutTimezone(startOfYear(date)).getTime();
    default:
      throw new Error(`Resolution ${desiredResolution} not supported`);
  }
};

export const unixTimeToDesiredResolutionDateString = (
  unixTime: number,
  desiredResolution: Resolution,
) => {
  const date = new Date(unixTime);
  switch (desiredResolution) {
    case Resolution.Daily:
      return formatDate(date, DateFormat.Daily);
    case Resolution.Weekly:
      return formatDate(
        dateWithoutTimezone(startOfWeek(date, { weekStartsOn: 1 })),
        DateFormat.Weekly,
      );
    case Resolution.Monthly:
      return formatDate(
        dateWithoutTimezone(startOfMonth(date)),
        DateFormat.Monthly,
      );
    case Resolution.Yearly:
      return formatDate(
        dateWithoutTimezone(startOfYear(date)),
        DateFormat.Yearly,
      );
    default:
      throw new Error(`Resolution ${desiredResolution} not supported`);
  }
};

export const enrichMinDate = (
  minDate: string,
  resolution: Resolution,
): string => {
  let parsedDate = parseISO(minDate);
  if (resolution === Resolution.Monthly) {
    parsedDate = startOfMonth(parsedDate);
  } else if (resolution === Resolution.Yearly) {
    parsedDate = startOfYear(parsedDate);
  } else if (resolution === Resolution.Weekly) {
    parsedDate = startOfWeek(parsedDate, { weekStartsOn: 1 });
  }
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;
  return formattedDate;
};

export const enrichMaxDate = (
  maxDate: string,
  exportResolution: Resolution,
): string => {
  let parsedDate = parseISO(maxDate);

  if (exportResolution === Resolution.Monthly) {
    parsedDate = endOfMonth(parsedDate);
  } else if (exportResolution === Resolution.Yearly) {
    parsedDate = endOfYear(parsedDate);
  } else if (exportResolution === Resolution.Weekly) {
    parsedDate = endOfWeek(parsedDate, { weekStartsOn: 1 });
  }
  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;

  return formattedDate;
};

export const dailyToDesiredResolutionDate = (
  dailyDate: string,
  desiredResolution: Resolution,
) => {
  const date = new Date(dailyDate);
  switch (desiredResolution) {
    case Resolution.Daily:
      return date;
    case Resolution.Weekly:
      return dateWithoutTimezone(
        new Date(
          date.getFullYear(),
          date.getMonth(),
          date.getDate() - date.getDay() + 1,
        ),
      );
    case Resolution.Monthly:
      return dateWithoutTimezone(
        new Date(date.getFullYear(), date.getMonth(), 1),
      );
    case Resolution.Yearly:
      return dateWithoutTimezone(new Date(date.getFullYear(), 0, 1));
    default:
      throw new Error(`Resolution ${desiredResolution} not supported`);
  }
};

export const givenResolutionToDailyDateString = (
  date: string,
  givenResolution: Resolution,
) => {
  const parsedDate = new Date(date);
  let resDate;
  switch (givenResolution) {
    case Resolution.Daily:
      resDate = parsedDate;
      break;
    case Resolution.Weekly:
      resDate = endOfWeek(parsedDate, { weekStartsOn: 1 });
      break;
    case Resolution.Monthly:
      resDate = endOfMonth(parsedDate);
      break;
    case Resolution.Yearly:
      resDate = endOfYear(parsedDate);
      break;
    default:
      throw new Error(`Resolution ${givenResolution} not supported`);
  }
  return format(resDate, DateFormat.Daily);
};

export const formatDateForExport = (date: Date, resolution: Resolution) => {
  let formattedDate;
  switch (resolution) {
    case Resolution.Daily:
      formattedDate = formatDate(date, DateFormat.Daily);
      break;
    case Resolution.Weekly:
      const isoYear = getISOWeekYear(date);
      const isoWeek = getISOWeek(date);
      formattedDate = `${isoYear}-W${String(isoWeek).padStart(2, '0')}`;
      break;
    case Resolution.Monthly:
      formattedDate = formatDate(startOfMonth(date), DateFormat.Monthly);
      break;
    case Resolution.Yearly:
      formattedDate = formatDate(startOfYear(date), DateFormat.Yearly);
      break;
    default:
      formattedDate = formatDate(date, DateFormat.Daily);
      break;
  }
  return formattedDate;
};

// function to determine depending on resolution (daily, weekly, monthly, yearly) how many of the respective resolutions respective timeunits are between two dates
export const getNumberOfTimeUnitsBetweenDates = (
  startDate: Date,
  endDate: Date,
  resolution: Resolution,
  adjustDatesAccordingToResolution = false,
): number => {
  let numberOfTimeUnits = 0;
  if (adjustDatesAccordingToResolution) {
    switch (resolution) {
      case Resolution.Daily:
        break;
      case Resolution.Weekly:
        startDate = startOfWeek(startDate, { weekStartsOn: 1 });
        endDate = endOfWeek(endDate, { weekStartsOn: 1 });
        break;
      case Resolution.Monthly:
        startDate = startOfMonth(startDate);
        endDate = endOfMonth(endDate);
        break;
      case Resolution.Yearly:
        startDate = startOfYear(startDate);
        endDate = endOfYear(endDate);
        break;
      default:
        throw new Error(`Resolution ${resolution} not supported`);
    }
  }
  let currentDate = startDate;

  while (currentDate <= endDate) {
    numberOfTimeUnits++;
    switch (resolution) {
      case Resolution.Daily:
        currentDate = addDays(currentDate, 1);
        break;
      case Resolution.Weekly:
        currentDate = addWeeks(currentDate, 1);
        break;
      case Resolution.Monthly:
        currentDate = addMonths(currentDate, 1);
        break;
      case Resolution.Yearly:
        currentDate = addYears(currentDate, 1);
        break;
      default:
        throw new Error(`Resolution ${resolution} not supported`);
    }
  }

  return numberOfTimeUnits;
};
